import type { ReactNode } from 'react';
import { Icon, type IconName } from '../icons/icons.js';
import styles from './designer.module.css';

/**
 * THE SENIOR DESIGNER'S PHASE 1 ANATOMY, ported from their React source.
 *
 * WHERE THIS CAME FROM. The designer's own implementation, read as code: the
 * six Phase 1 pages and the three dashboard components they compose from. The
 * CSS module beside this file records the literal inline declaration every
 * rule came from, so the port can be checked against their project without
 * opening it.
 *
 * WHY IT IS NOT `records/`. Screens 03, 05, 06, 07, 31, 32 and 33 are composed
 * from `records/` and keep it exactly as it was accepted — §20 forbids
 * regressing them to integrate Phase 1. Two anatomies coexist on purpose
 * while the designer's pack advances screen by screen.
 *
 * NOTHING HERE COMPUTES A FACT. Values arrive formatted. The one thing that
 * resembles arithmetic is the sparkline, which maps an already-exact series
 * onto a 70x32 box — drawing, not accounting.
 *
 * NOTHING HERE DECIDES WHAT A CALLER MAY SEE. Every action is a prop. A list
 * given no `onNew` renders no New button, and the route guard and the server
 * refuse the same request again either way.
 *
 * TWO VISIBLE VARIANCES FROM THE DESIGNER, BOTH FORCED AND BOTH RECORDED.
 * Their glyphs are drawn at `weight="fill"` and `"bold"`; §16 of the frozen
 * design system freezes `regular` for every interface icon and the `Icon` type
 * enforces it, so these are regular. And `--color-text-muted` (#94A3B8) is not
 * used for the captions it was written for, because it measures 2.56:1 — see
 * the token layer.
 */

export type BadgeTone = 'success' | 'warning' | 'danger' | 'info' | 'neutral';

const BADGE_CLASS: Readonly<Record<BadgeTone, string>> = {
  success: styles.badge_success ?? '',
  warning: styles.badge_warning ?? '',
  danger: styles.badge_danger ?? '',
  info: styles.badge_info ?? '',
  neutral: styles.badge_neutral ?? '',
};

/* ---------- page body ------------------------------------------------- */

/**
 * The page body.
 *
 * NO TITLE, NO SUBTITLE. Each designer page opens with its own `<h1>` and
 * standfirst; the shell already renders both from its route table, so porting
 * theirs would print the page's name twice. §3 and §14 forbid it.
 */
export function Page({ children }: { readonly children: ReactNode }) {
  return <div className={styles.page}>{children}</div>;
}

/** What sat beside the designer's dropped title: the date pill, the actions. */
export function ActionRow({ children }: { readonly children: ReactNode }) {
  return <div className={styles.actionRow}>{children}</div>;
}

/* ---------- metric band ----------------------------------------------- */

/**
 * The KPI strip.
 *
 * `columns` exists for the dashboard's Executive Snapshot alone, where the
 * designer sets the column count from how many cards the caller is entitled
 * to. Everywhere else it is the four §15 fixes in one desktop row.
 */
export function MetricBand({
  columns,
  children,
}: {
  readonly columns?: number;
  readonly children: ReactNode;
}) {
  if (columns === undefined) {
    return <div className={styles.metricBand}>{children}</div>;
  }
  return (
    <div
      className={`${styles.metricBand ?? ''} ${styles.metricBand_counted ?? ''}`}
      style={{ ['--designer-metric-columns' as string]: String(Math.max(columns, 1)) }}
    >
      {children}
    </div>
  );
}

export interface MetricCardProps {
  readonly label: string;
  /** Already formatted. A refused metric passes `null` and says so instead. */
  readonly value: string | null;
  readonly icon: IconName;
  /** The tile's ground. One of the frozen semantic colours, by name. */
  readonly tone?: BadgeTone | 'teal';
  /** Formatted, e.g. "12%". Absent when there is nothing to compare against. */
  readonly change?: string | undefined;
  readonly changeDirection?: 'up' | 'down' | undefined;
  /** What the change is against, e.g. "vs. 01–31 Mar 2026". */
  readonly caption?: string | undefined;
  /**
   * The metric's own series. ABSENT MEANS ABSENT: the designer hard-codes a
   * flattering curve per card, and a drawn line with nothing behind it is a
   * claim about a trend nobody measured.
   */
  readonly points?: readonly number[] | undefined;
  /** Why the figure is missing, when the server refused it. */
  readonly unavailable?: string | undefined;
}

/**
 * The executive card's tile, which is TINTED where the others are solid: the
 * designer gives it `iconBg: '#E7F5EF'` with `iconColor: '#0F7A57'`. Those are
 * the v1.4 semantic surfaces, by token name.
 */
const TILE_TINT: Readonly<Record<BadgeTone | 'teal', string>> = {
  teal: 'var(--color-brand-teal-soft)',
  success: 'var(--color-success-bg)',
  warning: 'var(--color-warning-bg)',
  danger: 'var(--color-danger-bg)',
  info: 'var(--color-info-bg)',
  neutral: 'var(--color-workspace)',
};

/**
 * The glyph's ink on that tinted tile.
 *
 * MEASURED, NOT ASSUMED. v1.4 chose each semantic pair for at least 4.5:1 of
 * its own surface, so `--color-success` on `--color-success-bg` is already
 * safe and so are the other three. Teal is the exception: the frozen system
 * carries a dedicated AA ink for text on `--color-brand-teal-soft`, and the
 * brand teal itself is not it.
 */
const TILE_INK: Readonly<Record<BadgeTone | 'teal', string>> = {
  teal: 'var(--x-teal-ink-on-soft)',
  success: 'var(--color-success)',
  warning: 'var(--color-warning)',
  danger: 'var(--color-danger)',
  info: 'var(--color-info)',
  neutral: 'var(--color-text-head)',
};

const TILE_GROUND: Readonly<Record<BadgeTone | 'teal', string>> = {
  teal: 'var(--color-brand-teal)',
  success: 'var(--color-success)',
  warning: 'var(--color-warning)',
  danger: 'var(--color-danger)',
  info: 'var(--color-info)',
  neutral: 'var(--color-text-head)',
};

export function MetricCard({
  label,
  value,
  icon,
  tone = 'teal',
  change,
  changeDirection,
  caption,
  points,
  unavailable,
}: MetricCardProps) {
  return (
    <div className={styles.metricCard}>
      <div className={styles.metricMain}>
        <div className={styles.metricHead}>
          <span
            className={styles.metricTile}
            style={{ ['--designer-tile' as string]: TILE_GROUND[tone] }}
          >
            <Icon name={icon} size={20} />
          </span>
          <span className={styles.metricLabel}>{label}</span>
        </div>

        {/*
          A REFUSED FIGURE SAYS SO. `total: null` from the server means the
          caller may not read it — not that it is zero. Printing 0 would be a
          statement nobody made.

          THE FIGURE CARRIES ITS OWN NAME, because the label above it is a
          separate element: without this, the only way to ask for one card's
          total among five is to know which was on screen. The product's other
          KPI cards have always done this and a reader using assistive
          technology hears the same thing on every one.
        */}
        <div
          className={styles.metricValue}
          aria-label={`${label} value`}
          title={value ?? undefined}
        >
          {value ?? '—'}
        </div>

        {unavailable !== undefined ? (
          <div className={styles.metricRefused}>
            {/*
              THE FIXED HEADING THE PRODUCT ALREADY USES for exactly this, so a
              figure refused here and refused on the dashboard read as one thing
              happening rather than two.
            */}
            <span className={styles.metricRefusedTitle}>Not available for your access</span>
            <span className={styles.metricCaption}>{unavailable}</span>
          </div>
        ) : change !== undefined && changeDirection !== undefined ? (
          <div
            className={`${styles.metricTrend ?? ''} ${
              (changeDirection === 'up' ? styles.metricTrend_up : styles.metricTrend_down) ?? ''
            }`}
          >
            <Icon name={changeDirection === 'up' ? 'trend-up' : 'trend-down'} size={14} />
            <span>{change}</span>
            {caption === undefined ? null : <span className={styles.metricCaption}>{caption}</span>}
          </div>
        ) : caption === undefined ? null : (
          /*
            A CAPTION WITH NO CHANGE BESIDE IT. The designer's card always
            carries a percentage, so their caption only ever appears after one.
            A metric with no previous period still has to say what the figure is
            of — "of the 200 most recent", "from the ageing" — and dropping that
            line because there is no percentage would leave the figure claiming
            more than it measures.
          */
          <div className={styles.metricTrend}>
            <span className={styles.metricCaption}>{caption}</span>
          </div>
        )}
      </div>

      {points === undefined || points.length < 2 ? null : (
        <Sparkline points={points} tone={changeDirection === 'down' ? 'danger' : 'success'} />
      )}
    </div>
  );
}

/**
 * The designer's sparkline, drawn from a real series.
 *
 * They hard-code a bezier per card. This maps the server's own points onto the
 * same 70x32 box and smooths it through the midpoints, which is the designer's
 * curve shape over data instead of over a guess. A flat series draws a flat
 * line, which is the honest picture of a flat month.
 */
export function Sparkline({
  points,
  tone = 'success',
  shape = 'smooth',
  size = 'list',
}: {
  readonly points: readonly number[];
  readonly tone?: 'success' | 'danger';
  /**
   * THE DESIGNER DRAWS BOTH, AND NOT INTERCHANGEABLY. Their list and overview
   * cards carry a smoothed bezier; their executive cards carry a straight
   * `<polyline>` at 62x24, which is a different drawing at a different size
   * rather than the same one scaled.
   */
  readonly shape?: 'smooth' | 'linear';
  readonly size?: 'list' | 'executive';
}) {
  const width = size === 'executive' ? 62 : 70;
  const height = size === 'executive' ? 24 : 32;
  const pad = 3;
  const low = Math.min(...points);
  const high = Math.max(...points);
  const span = high - low;
  const step = points.length > 1 ? (width - pad * 2) / (points.length - 1) : 0;

  const xy = points.map((value, index) => {
    const x = pad + step * index;
    // A flat series sits on the midline rather than dividing by zero.
    const ratio = span === 0 ? 0.5 : (value - low) / span;
    const y = height - pad - ratio * (height - pad * 2);
    return [x, y] as const;
  });

  let path = `M ${String(xy[0]?.[0] ?? pad)} ${String(xy[0]?.[1] ?? height / 2)}`;
  if (shape === 'linear') {
    for (const [x, y] of xy.slice(1)) path += ` L ${String(x)} ${String(y)}`;
  }
  for (let index = 1; shape === 'smooth' && index < xy.length; index += 1) {
    const previous = xy[index - 1];
    const current = xy[index];
    if (previous === undefined || current === undefined) continue;
    const midX = (previous[0] + current[0]) / 2;
    path += ` Q ${String(previous[0])} ${String(previous[1])} ${String(midX)} ${String(
      (previous[1] + current[1]) / 2,
    )}`;
    path += ` Q ${String(current[0])} ${String(current[1])} ${String(current[0])} ${String(
      current[1],
    )}`;
  }

  return (
    <svg
      className={size === 'executive' ? styles.executiveSpark : styles.metricSpark}
      viewBox={`0 0 ${String(width)} ${String(height)}`}
      fill="none"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d={path}
        stroke={tone === 'danger' ? 'var(--color-danger)' : 'var(--color-success)'}
        strokeWidth={size === 'executive' ? '1.75' : '2.5'}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/* ---------- the executive snapshot (Screen 00) ------------------------- */

/**
 * The dashboard's own band, which is NOT the four-card strip.
 *
 * The designer sets `repeat(${visibleKpis.length}, minmax(0, 1fr))` — as many
 * columns as the caller is entitled to cards, up to six — so the count comes
 * from the data and is still an exact count rather than `auto-fit`. A caller
 * who can reach two metrics gets two slots of a six-slot row filled, which
 * reads as a dashboard with four things missing rather than two half-width
 * cards.
 */
export function ExecutiveBand({
  columns,
  children,
}: {
  readonly columns: number;
  readonly children: ReactNode;
}) {
  return (
    <div
      className={styles.executiveBand}
      style={{ ['--designer-executive-columns' as string]: String(Math.max(columns, 1)) }}
    >
      {children}
    </div>
  );
}

/**
 * The designer's executive card — `components/ExecutiveSnapshot.tsx`.
 *
 * A DIFFERENT CARD FROM `MetricCard`, which is the designer's own doing: six of
 * these sit in a row where four metric cards sit, so the tile is 32px and
 * tinted rather than 40px and solid, the figure is 18.5px rather than 24, and
 * the label sits BESIDE the tile instead of under it. Their list card and their
 * executive card are two compositions in one package and this module carries
 * both rather than stretching one over both jobs.
 */
export function ExecutiveCard({
  label,
  value,
  icon,
  tone = 'teal',
  change,
  changeDirection,
  caption,
  points,
  unavailable,
  segments,
}: MetricCardProps & {
  /**
   * A BREAKDOWN IN PLACE OF THE TREND, for a metric that is dimensioned rather
   * than a series. The designer's six are all series, so they draw no such
   * card; the server sends one — tasks by priority — and a dimensioned metric
   * plotted as a line would be three priorities drawn as one trend.
   *
   * IT IS THE LEGEND AND NOT THE RING, which is a measurement rather than a
   * preference. Six of these sit in a 145px row and the ring Screen 01 draws is
   * 72px before its own legend; put one here and the capture harness shows the
   * arc cut off by the card's lower edge. The legend alone says every figure
   * the ring would have drawn, in words, and fits.
   */
  readonly segments?: readonly RingSegment[];
}) {
  if (unavailable !== undefined) {
    return (
      <div className={styles.executiveCard}>
        <div className={styles.executiveHead}>
          <span className={`${styles.executiveTile ?? ''} ${styles.executiveTile_neutral ?? ''}`}>
            <Icon name={icon} size={18} />
          </span>
          <span className={styles.executiveText}>
            <span className={styles.executiveLabel}>{label}</span>
            {/* The product's fixed refusal heading, as on every other card. */}
            <span className={styles.metricRefusedTitle}>Not available for your access</span>
          </span>
        </div>
        <span className={styles.executiveRefused}>{unavailable}</span>
      </div>
    );
  }

  return (
    <div className={styles.executiveCard}>
      <div className={styles.executiveHead}>
        <span
          className={styles.executiveTile}
          style={{
            ['--designer-tile' as string]: TILE_TINT[tone],
            ['--designer-tile-ink' as string]: TILE_INK[tone],
          }}
        >
          <Icon name={icon} size={18} />
        </span>
        <span className={styles.executiveText}>
          <span className={styles.executiveLabel}>{label}</span>
          <span className={styles.executiveValue} aria-label={`${label} value`}>
            {value ?? '—'}
          </span>
        </span>
      </div>

      {segments !== undefined && segments.length > 1 ? (
        <ul className={`${styles.ringLegend ?? ''} ${styles.executiveLegend ?? ''}`}>
          {segments.map((segment) => (
            <li key={segment.label} className={styles.ringRow}>
              <span
                className={styles.ringDot}
                aria-hidden="true"
                style={{ ['--designer-segment' as string]: SEGMENT_INK[segment.tone] }}
              />
              <span className={styles.ringCount}>
                {new Intl.NumberFormat('en-GB').format(segment.value)}
              </span>
              <span className={styles.ringLabel}>{segment.label}</span>
            </li>
          ))}
        </ul>
      ) : (
        <div className={styles.executiveFoot}>
          <span className={styles.executiveTrendBlock}>
            {change === undefined || changeDirection === undefined ? null : (
              <span
                className={`${styles.executiveTrend ?? ''} ${
                  (changeDirection === 'up'
                    ? styles.executiveTrend_up
                    : styles.executiveTrend_down) ?? ''
                }`}
              >
                <Icon name={changeDirection === 'up' ? 'trend-up' : 'trend-down'} size={13} />
                <span>{change}</span>
              </span>
            )}
            {caption === undefined ? null : (
              <span className={styles.executiveCaption}>{caption}</span>
            )}
          </span>
          {points === undefined || points.length < 2 ? null : (
            <Sparkline
              points={points}
              shape="linear"
              size="executive"
              tone={changeDirection === 'down' ? 'danger' : 'success'}
            />
          )}
        </div>
      )}
    </div>
  );
}

/* ---------- the ringed metric card ------------------------------------ */

/*
 * A SEGMENT'S INK: the five semantic tones, the brand, or one of the four
 * categorical chart colours.
 *
 * v1.2 added that categorical palette precisely so that a dimension with no
 * semantic meaning — a priority, a department, an account — is not drawn in
 * `danger`, which would invite a reader to see a problem nobody reported.
 * Paid really is green and overdue really is red; "Normal" is not amber.
 */
export type SegmentInk =
  BadgeTone | 'teal' | 'accent' | 'series1' | 'series2' | 'series3' | 'series4';

const SEGMENT_INK: Readonly<Record<SegmentInk, string>> = {
  teal: 'var(--color-brand-teal)',
  /*
   * v1.2 ADDED AN ACCENT CHIP AND DELIBERATELY NO ACCENT INK — "an accent chip
   * carries `--color-text-head`, so a seventh status cannot quietly become a
   * seventh brand colour". A dot is ink, so it takes that same ink rather than
   * the violet the chip is tinted with.
   */
  accent: 'var(--color-text-head)',
  success: 'var(--color-success)',
  warning: 'var(--color-warning)',
  danger: 'var(--color-danger)',
  info: 'var(--color-info)',
  neutral: 'var(--color-text-head)',
  series1: 'var(--x-chart-series-1)',
  series2: 'var(--x-chart-series-2)',
  series3: 'var(--x-chart-series-3)',
  series4: 'var(--x-chart-series-4)',
};

export interface RingSegment {
  readonly label: string;
  readonly value: number;
  readonly tone: SegmentInk;
}

/**
 * The designer's second Screen 01 card: a legend and a donut.
 *
 * ONE CARD SHELL FOR BOTH SHAPES, which is the designer's own doing — their
 * ringed card is the ordinary metric card with the sparkline swapped for a
 * 72px ring. Keeping it in this module is what stops Screen 01's band from
 * being a different card from Screens 04, 08 and 10's.
 *
 * THE RING DRAWS THE SERVER'S OWN BREAKDOWN. A dimensioned metric already
 * carries one figure per status; the arcs are those figures as shares of their
 * own sum, so nothing here counts anything. With no segments summing above
 * zero there is nothing to divide, and the track is drawn alone rather than
 * one arc pretending to be a whole.
 */
export function MetricRing({
  label,
  icon,
  tone = 'info',
  total,
  segments,
}: {
  readonly label: string;
  readonly icon: IconName;
  readonly tone?: BadgeTone | 'teal';
  /** Already formatted, and `null` where the server refused it. */
  readonly total: string | null;
  readonly segments: readonly RingSegment[];
}) {
  const sum = segments.reduce((running, segment) => running + segment.value, 0);
  let offset = 0;
  return (
    <div className={styles.metricCard}>
      <div className={styles.metricRingBody}>
        <div className={styles.metricHead}>
          <span
            className={styles.metricTile}
            style={{ ['--designer-tile' as string]: TILE_GROUND[tone] }}
          >
            <Icon name={icon} size={20} />
          </span>
          <span className={styles.metricLabel}>{label}</span>
        </div>
        <ul className={styles.ringLegend}>
          {segments.map((segment) => (
            <li key={segment.label} className={styles.ringRow}>
              <span
                className={styles.ringDot}
                aria-hidden="true"
                style={{ ['--designer-segment' as string]: SEGMENT_INK[segment.tone] }}
              />
              <span className={styles.ringCount}>
                {new Intl.NumberFormat('en-GB').format(segment.value)}
              </span>
              <span className={styles.ringLabel}>{segment.label}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className={styles.ringChart}>
        {/*
          THE ARCS ARE DECORATIVE AND `aria-hidden` FOR IT: every figure in the
          ring is in the legend beside it, in words. THE TOTAL IN THE CENTRE IS
          NOT — it is the card's own figure, and the designer puts it inside the
          ring rather than above it, so only the drawing is hidden.
        */}
        <svg
          width="72"
          height="72"
          viewBox="0 0 36 36"
          aria-hidden="true"
          style={{ transform: 'rotate(-90deg)' }}
        >
          <circle cx="18" cy="18" r="14" fill="none" stroke="var(--color-border)" strokeWidth="4" />
          {sum <= 0
            ? null
            : segments.map((segment) => {
                const share = (segment.value / sum) * 100;
                const dash = `${String(share)} ${String(100 - share)}`;
                const at = offset;
                offset -= share;
                return (
                  <circle
                    key={segment.label}
                    cx="18"
                    cy="18"
                    r="14"
                    fill="none"
                    stroke={SEGMENT_INK[segment.tone]}
                    strokeWidth="4"
                    strokeDasharray={dash}
                    strokeDashoffset={String(at)}
                  />
                );
              })}
        </svg>
        {/*
          THE TOTAL ALONE IN THE CENTRE. The designer sets a word under it at
          8px — "48 / Invoices" — and at any size the frozen scale actually
          sets type at, that word is wider than the 48 pixels inside a 72px
          ring: a capture of Screen 01 showed "Invoices" crossing the arc. The
          card's own label two lines above says what is being counted, so the
          word is not needed twice, least of all on top of the drawing. Same
          decision the dashboard's cards made, for the same reason.
        */}
        <span className={styles.ringCentre}>
          <span className={styles.ringTotal} aria-label={`${label} value`}>
            {total ?? '—'}
          </span>
        </span>
      </div>
    </div>
  );
}

/* ---------- filter bar ------------------------------------------------ */

export function FilterBar({ children }: { readonly children: ReactNode }) {
  return <div className={styles.filterBar}>{children}</div>;
}

export function FilterControls({ children }: { readonly children: ReactNode }) {
  return <div className={styles.filterControls}>{children}</div>;
}

/** The designer's 320px search field, with the glyph inside it. */
export function Search({
  value,
  onChange,
  placeholder,
  label,
}: {
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly placeholder: string;
  /** Named for assistive technology; the designer draws no visible label. */
  readonly label: string;
}) {
  return (
    <div className={styles.search}>
      <span className={styles.searchIcon} aria-hidden="true">
        <Icon name="magnifying-glass" size={16} />
      </span>
      <input
        className={styles.searchInput}
        type="search"
        value={value}
        aria-label={label}
        placeholder={placeholder}
        onChange={(event) => {
          onChange(event.target.value);
        }}
      />
    </div>
  );
}

/**
 * A filter, with the designer's small label above it.
 *
 * THEIRS IS A BUTTON THAT OPENS A PANEL; this is a native select. It carries
 * the same affordance, is keyboard- and screen-reader-complete without
 * re-implementing a listbox, and cannot leave a menu open behind a route
 * change. The label, the height and the type are the designer's.
 */
export function FilterSelect({
  label,
  value,
  options,
  onChange,
}: {
  readonly label: string;
  readonly value: string;
  readonly options: readonly { readonly value: string; readonly label: string }[];
  readonly onChange: (value: string) => void;
}) {
  return (
    <label className={styles.filterField}>
      <span className={styles.filterLabel}>{label}</span>
      <select
        className={styles.filterSelect}
        value={value}
        onChange={(event) => {
          onChange(event.target.value);
        }}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}

/**
 * The designer's date range: one bordered shell, a calendar glyph and two
 * native date inputs.
 *
 * NATIVE INPUTS, FOR THE SAME REASON `FilterSelect` IS A NATIVE SELECT. A date
 * picker re-implemented in a list screen is a keyboard trap waiting to happen,
 * and the designer's own markup is two `input type="date"` elements in a
 * flex row — this is their markup, with the frozen tokens for the chrome.
 */
export function DateRange({
  fromLabel,
  toLabel,
  from,
  to,
  onFrom,
  onTo,
  label,
}: {
  readonly label: string;
  /** Named for assistive technology; the designer draws no visible labels. */
  readonly fromLabel: string;
  readonly toLabel: string;
  readonly from: string;
  readonly to: string;
  readonly onFrom: (value: string) => void;
  readonly onTo: (value: string) => void;
}) {
  return (
    <div className={styles.filterField}>
      <span className={styles.filterLabel}>{label}</span>
      <div className={styles.dateRange}>
        <span className={styles.dateRangeIcon} aria-hidden="true">
          <Icon name="calendar-blank" size={15} />
        </span>
        <input
          className={styles.dateInput}
          type="date"
          value={from}
          aria-label={fromLabel}
          onChange={(event) => {
            onFrom(event.target.value);
          }}
        />
        <span className={styles.dateRangeTo} aria-hidden="true">
          →
        </span>
        <input
          className={styles.dateInput}
          type="date"
          value={to}
          aria-label={toLabel}
          onChange={(event) => {
            onTo(event.target.value);
          }}
        />
      </div>
    </div>
  );
}

/* ---------- actions --------------------------------------------------- */

export function Action({
  children,
  onClick,
  variant = 'outlined',
  icon,
  disabled,
  type = 'button',
}: {
  readonly children: ReactNode;
  readonly onClick?: (() => void) | undefined;
  readonly variant?: 'ghost' | 'outlined' | 'primary';
  readonly icon?: IconName | undefined;
  readonly disabled?: boolean | undefined;
  readonly type?: 'button' | 'submit';
}) {
  const variantClass =
    variant === 'ghost'
      ? styles.action_ghost
      : variant === 'primary'
        ? styles.action_primary
        : styles.action_outlined;
  return (
    <button
      type={type}
      className={`${styles.action ?? ''} ${variantClass ?? ''}`}
      disabled={disabled ?? false}
      {...(onClick === undefined ? {} : { onClick })}
    >
      {icon === undefined ? null : <Icon name={icon} size={15} />}
      <span>{children}</span>
    </button>
  );
}

/* ---------- list card ------------------------------------------------- */

export function ListCard({ children }: { readonly children: ReactNode }) {
  return <div className={styles.listCard}>{children}</div>;
}

/** The designer's "Customers (N)" strip. The count is the real one. */
export function ListHead({
  title,
  count,
  children,
}: {
  readonly title: string;
  readonly count?: number | undefined;
  readonly children?: ReactNode;
}) {
  return (
    <div className={styles.listHead}>
      <h2 className={styles.listTitle}>
        {count === undefined ? title : `${title} (${String(count)})`}
      </h2>
      {children}
    </div>
  );
}

export function ListScroll({ children }: { readonly children: ReactNode }) {
  return <div className={styles.listScroll}>{children}</div>;
}

export function Table({ children }: { readonly children: ReactNode }) {
  return <table className={styles.table}>{children}</table>;
}

export function Thead({ children }: { readonly children: ReactNode }) {
  return (
    <thead className={styles.thead}>
      <tr>{children}</tr>
    </thead>
  );
}

export function Th({
  children,
  align,
}: {
  readonly children: ReactNode;
  readonly align?: 'right' | 'centre';
}) {
  const alignClass =
    align === 'right' ? styles.thRight : align === 'centre' ? styles.thCentre : undefined;
  return <th className={alignClass ?? undefined}>{children}</th>;
}

export function Row({ children }: { readonly children: ReactNode }) {
  return <tr className={styles.row}>{children}</tr>;
}

export function Td({
  children,
  align,
  muted,
  detail,
}: {
  readonly children: ReactNode;
  readonly align?: 'right' | 'centre';
  readonly muted?: boolean;
  /**
   * The designer's quieter second line inside a cell — the days a due date is
   * past, for instance. It is a second fact about the same row, not a second
   * column, which is why it lives here rather than widening the table.
   */
  readonly detail?: ReactNode;
}) {
  const classes = [
    align === 'right' ? styles.tdRight : align === 'centre' ? styles.tdCentre : undefined,
    muted === true ? styles.tdMuted : undefined,
  ].filter((value): value is string => value !== undefined);
  return (
    <td className={classes.join(' ')}>
      {children}
      {detail === undefined ? null : <div className={styles.cellDetail}>{detail}</div>}
    </td>
  );
}

/** A money cell. Right-aligned, tabular, and it never wraps mid-amount. */
export function MoneyCell({
  children,
  detail,
}: {
  readonly children: ReactNode;
  /** A second fact under the figure — what a credit limit does, for instance. */
  readonly detail?: ReactNode;
}) {
  return (
    <td className={styles.tdMoney}>
      {children}
      {detail === undefined ? null : <div className={styles.cellDetail}>{detail}</div>}
    </td>
  );
}

/* ---------- entity cell ----------------------------------------------- */

/**
 * The designer's name cell: an initials tile, the name, and a quieter line
 * under it. `onOpen` is what makes the name a control — a list given none
 * renders text, because there is nowhere for it to go.
 *
 * THE TILE IS OPTIONAL BECAUSE THE DESIGNER DRAWS IT ON SOME LISTS AND NOT
 * OTHERS. Their customers list gives every row an initials tile; their sales
 * invoices list draws the invoice number as a link over its reference, and the
 * customer as a bold name, with no tile on either. Passing no initials is that
 * second cell, rather than a second component that would drift from this one.
 */
export function EntityCell({
  name,
  meta,
  initials,
  tone,
  onOpen,
}: {
  readonly name: string;
  readonly meta?: string | undefined;
  readonly initials?: string | undefined;
  readonly tone?: string | undefined;
  readonly onOpen?: (() => void) | undefined;
}) {
  return (
    <div className={styles.entity}>
      {initials === undefined ? null : (
        <span
          className={styles.initials}
          aria-hidden="true"
          {...(tone === undefined ? {} : { style: { ['--designer-tile' as string]: tone } })}
        >
          {initials}
        </span>
      )}
      <span className={styles.entityText}>
        {onOpen === undefined ? (
          <span className={styles.entityName}>{name}</span>
        ) : (
          <button type="button" className={styles.entityLink} onClick={onOpen} title={name}>
            {name}
          </button>
        )}
        {meta === undefined ? null : <span className={styles.entityMeta}>{meta}</span>}
      </span>
    </div>
  );
}

/* ---------- badge ----------------------------------------------------- */

export function Badge({
  children,
  tone = 'neutral',
  icon,
}: {
  readonly children: ReactNode;
  readonly tone?: BadgeTone;
  readonly icon?: IconName | undefined;
}) {
  return (
    <span className={`${styles.badge ?? ''} ${BADGE_CLASS[tone]}`}>
      {icon === undefined ? null : <Icon name={icon} size={13} />}
      {children}
    </span>
  );
}

/* ---------- tabs ------------------------------------------------------ */

export interface ListTab {
  readonly id: string;
  readonly label: string;
  readonly count: number;
}

export function TabStrip({
  tabs,
  active,
  onSelect,
  label,
}: {
  readonly tabs: readonly ListTab[];
  readonly active: string;
  readonly onSelect: (id: string) => void;
  readonly label: string;
}) {
  return (
    <div className={styles.tabStrip} role="tablist" aria-label={label}>
      {tabs.map((tab) => {
        const selected = tab.id === active;
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={selected}
            className={`${styles.tab ?? ''} ${selected ? (styles.tabActive ?? '') : ''}`}
            onClick={() => {
              onSelect(tab.id);
            }}
          >
            <span>{tab.label}</span>
            <span className={styles.tabCount}>{tab.count}</span>
          </button>
        );
      })}
    </div>
  );
}

/* ---------- footer and pager ------------------------------------------ */

export function ListFoot({ children }: { readonly children: ReactNode }) {
  return <div className={styles.listFoot}>{children}</div>;
}

/**
 * The designer's page controls.
 *
 * PAGING IS OVER WHAT THE SERVER SENT. These lists read an authorised page and
 * page it in the browser; the control reports the window it is showing, not a
 * total it inferred. Where a read is capped, the caller says so beside it
 * rather than letting the last page imply the list ended.
 */
export function Pager({
  page,
  pages,
  onPage,
}: {
  readonly page: number;
  readonly pages: number;
  readonly onPage: (page: number) => void;
}) {
  if (pages <= 1) return null;
  const numbers: number[] = [];
  for (let index = 1; index <= pages; index += 1) numbers.push(index);
  const shown =
    pages <= 7 ? numbers : [1, page - 1, page, page + 1, pages].filter((n) => n >= 1 && n <= pages);
  const unique = [...new Set(shown)].sort((a, b) => a - b);

  return (
    <nav className={styles.pager} aria-label="Pages">
      <button
        type="button"
        className={styles.pageButton}
        disabled={page <= 1}
        onClick={() => {
          onPage(page - 1);
        }}
        aria-label="Previous page"
      >
        <Icon name="caret-left" size={13} />
      </button>
      {unique.map((number) => (
        <button
          key={number}
          type="button"
          className={`${styles.pageButton ?? ''} ${number === page ? (styles.pageCurrent ?? '') : ''}`}
          aria-label={`Page ${String(number)}`}
          aria-current={number === page ? 'page' : undefined}
          onClick={() => {
            onPage(number);
          }}
        >
          {number}
        </button>
      ))}
      <button
        type="button"
        className={styles.pageButton}
        disabled={page >= pages}
        onClick={() => {
          onPage(page + 1);
        }}
        aria-label="Next page"
      >
        <Icon name="caret-right" size={13} />
      </button>
    </nav>
  );
}

/* ---------- row actions ----------------------------------------------- */

export function RowActions({ children }: { readonly children: ReactNode }) {
  return <span className={styles.rowActions}>{children}</span>;
}

export function RowAction({
  icon,
  label,
  onClick,
}: {
  readonly icon: IconName;
  readonly label: string;
  readonly onClick: () => void;
}) {
  return (
    <button type="button" className={styles.rowAction} onClick={onClick} title={label}>
      <Icon name={icon} size={16} label={label} />
    </button>
  );
}
